import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { Avatar, Button, DatePicker, Message, Table, Tooltip } from "@arco-design/web-react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { formatModerationDateTime } from "../../../utils/meetingDateTime";

import { UserAvatar } from "../../../components/UserAvatar/UserAvatar";
import { downloadSelectedReports, ReportType } from "./downloadReports";
import { ReportsTypePicker } from "./ReportsTypePicker";
import { ScheduledPostsSection } from "./ScheduledPostsSection";
import { getStatsCol, getStatsTableScrollX } from "./statsTableLayout";
import { useBreakpoint } from "../../../utils/hooks/useBreakpoint";
import { StatisticsData } from "./types";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar.svg";
import { endpoints } from "../../../api/endpoints";
import { routes } from "../../../routes/routes";
import { RootState } from "../../../store/store";
import { useEventIntakeEnabled } from "../../../hooks/useEventIntakeEnabled";

import "./Statistics.css";
import classNames from "classnames";
import { currentThemeName } from "../../../theme";
import { getButtonColor, getButtonTextColor } from "../../../utils/cssVariables";

const MODERATION_PAGE_SIZE = 10;

type UserActionRow = {
  id: string;
  created_at: string;
  action_type: string;
  user_id: number;
  user_name: string;
  user_last_name?: string;
  target_user_id: number;
  target_name: string;
  target_last_name?: string;
  reason: string;
};

function formatModerationFullName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "—";
}

/** e.g. "Sarah J." */
function formatModerationShortName(firstName?: string, lastName?: string): string {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (!first && !last) return "—";
  if (!last) return first;
  return `${first} ${last.charAt(0).toUpperCase()}.`;
}

function renderModerationProfileLink(
  userId: number,
  firstName?: string,
  lastName?: string,
  display: "full" | "short" = "full"
) {
  const label =
    display === "short"
      ? formatModerationShortName(firstName, lastName)
      : formatModerationFullName(firstName, lastName);
  if (!userId || label === "—") {
    return <span className="statistics__moderation-name">{label}</span>;
  }
  return (
    <NavLink
      to={routes.ADMIN_USER.replace(":id", userId.toString())}
      className="statistics__moderation-profile-link"
      onClick={(e) => e.stopPropagation()}
      title={formatModerationFullName(firstName, lastName)}
    >
      {label}
    </NavLink>
  );
}

const fetchStatistics = async () => {
  const response = await axios.get(endpoints.admin.getStatistics);
  return response.data as StatisticsData;
};

const fetchUserActions = async () => {
  const response = await axios.get(endpoints.admin.getUserActions);
  return response.data as UserActionRow[];
};

function parseReportReason(reason: string): { category: string; details: string } | null {
  if (!reason) {
    return null;
  }
  try {
    const parsed = JSON.parse(reason) as { category?: string; details?: string };
    return {
      category: parsed.category || "",
      details: parsed.details || "",
    };
  } catch {
    return { category: "", details: reason };
  }
}

export const Statistics = () => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint("mobile");
  const statsCol = useMemo(() => getStatsCol(isMobile), [isMobile]);
  const statsScrollX = useMemo(() => getStatsTableScrollX(isMobile), [isMobile]);
  const qrSignupEnabled = useSelector((state: RootState) => state.config.qrSignupEnabled);
  const qrProfileEnabled = useSelector((state: RootState) => state.config.qrProfileEnabled);
  const eventIntakeEnabled = useEventIntakeEnabled();
  const showQrAnalytics = qrSignupEnabled || qrProfileEnabled;
  const [moderationPage, setModerationPage] = useState<number>(1);
  const [selectedReports, setSelectedReports] = useState<ReportType[]>(["platform"]);
  const [reportRange, setReportRange] = useState<Dayjs[] | undefined>(undefined);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const availableReportTypes = useMemo((): ReportType[] => {
    const types: ReportType[] = ["platform", "moderation", "user-detail"];
    if (showQrAnalytics) {
      types.push("qr");
    }
    return types;
  }, [showQrAnalytics]);

  const { data, isLoading, error: statisticsError } = useQuery({
    queryKey: ["statistics"],
    queryFn: fetchStatistics,
    retry: false,
  });

  const {
    data: moderationData,
    isLoading: moderationLoading,
    error: moderationError,
  } = useQuery({
    queryKey: ["admin-user-actions"],
    queryFn: fetchUserActions,
    retry: false,
  });

  useEffect(() => {
    if (statisticsError) {
      Message.error({ content: t("admin.statistics.load_error"), closable: true, duration: 4000 });
    }
  }, [statisticsError, t]);

  useEffect(() => {
    if (moderationError) {
      Message.error({ content: t("admin.moderation.load_error"), closable: true, duration: 4000 });
    }
  }, [moderationError, t]);

  useEffect(() => {
    if (!showQrAnalytics) {
      setSelectedReports((prev) => prev.filter((type) => type !== "qr"));
    }
  }, [showQrAnalytics]);

  const visibleModerationData = useMemo(() => {
    return (moderationData || []).filter((row) => row.action_type !== "delete");
  }, [moderationData]);

  const moderationColumns = useMemo(
    () => [
      {
        title: t("admin.moderation.col_time"),
        dataIndex: "created_at",
        width: statsCol.when,
        render: (value: string) => (
          <span className="statistics__moderation-date">{formatModerationDateTime(value)}</span>
        ),
      },
      {
        title: t("admin.moderation.col_reason"),
        dataIndex: "reason",
        width: statsCol.preview,
        ellipsis: true,
        render: (_: string, record: UserActionRow) => {
          const parsed = parseReportReason(record.reason);
          if (!parsed) {
            return "—";
          }
          const summary = parsed.category || parsed.details || "—";
          const tooltip = [parsed.category, parsed.details].filter(Boolean).join(" — ") || summary;
          if (summary === "—") {
            return summary;
          }
          return (
            <Tooltip content={tooltip} trigger={["hover", "focus", "click"]} position="top">
              <span className="statistics__moderation-reason">{summary}</span>
            </Tooltip>
          );
        },
      },
      {
        title: isMobile ? t("admin.moderation.col_actor_short") : t("admin.moderation.col_actor"),
        dataIndex: "user_name",
        width: statsCol.by,
        render: (_: string, record: UserActionRow) =>
          renderModerationProfileLink(
            record.user_id,
            record.user_name,
            record.user_last_name,
            "short"
          ),
      },
      {
        title: t("admin.moderation.col_target"),
        dataIndex: "target_name",
        width: statsCol.involved,
        render: (_: string, record: UserActionRow) =>
          renderModerationProfileLink(
            record.target_user_id,
            record.target_name,
            record.target_last_name,
            "full"
          ),
      },
      {
        title: t("admin.moderation.col_action"),
        dataIndex: "action_type",
        width: statsCol.actions,
        render: (value: string) => t(`admin.moderation.action_${value}`, { defaultValue: value }),
      },
    ],
    [t, statsCol, isMobile]
  );

  const getReportDateRange = () =>
    reportRange?.[0] && reportRange?.[1]
      ? {
          from: reportRange[0].startOf("day").format("YYYY-MM-DD"),
          to: reportRange[1].startOf("day").format("YYYY-MM-DD"),
        }
      : undefined;

  const runDownload = async (types: ReportType[]) => {
    if (types.length === 0) {
      Message.warning({ content: t("admin.statistics.reports_nothing_selected"), closable: true, duration: 4000 });
      return;
    }

    setIsDownloadingReport(true);
    try {
      await downloadSelectedReports(types, getReportDateRange());
    } catch (error) {
      console.error("Error downloading report", error);
      const errorKey =
        types.length === 1
          ? types[0] === "user-detail"
            ? "admin.statistics.user_detail_csv_download_error"
            : types[0] === "qr"
              ? "admin.statistics.qr_csv_download_error"
              : types[0] === "moderation"
                ? "notification_messages.moderation_csv_download_error"
                : "admin.statistics.platform_csv_download_error"
          : "admin.statistics.reports_workbook_download_error";
      Message.error({ content: t(errorKey), closable: true, duration: 4000 });
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleDownloadReport = () => runDownload(selectedReports);

  const handleFullReport = () => {
    const allTypes = [...availableReportTypes];
    setSelectedReports(allTypes);
    void runDownload(allTypes);
  };

  const renderTopUsersSkeletons = () => {
    const items = [1, 2, 3];

    return items.map((key, index) => (
      <div key={key} className="top-list-item">
        <p className="place">{index + 1}</p>
        <div className="user">
          <Avatar className="user-avatar" />
          <span className="user-name skeleton" />
        </div>
      </div>
    ));
  };

  const renderTopCategorySkeletons = () => {
    const items = [1, 2, 3];

    return items.map((key, index) => (
      <div key={key} className="top-list-item">
        <p className="place">{index + 1}</p>
        <span className="distribution-name skeleton" />
      </div>
    ));
  };

  return (
    <section className={classNames("statistics", {
      "statistics--black-theme": currentThemeName === "black",
    })}
    >
      <h1 className="statistics__title">{t("admin.statistics.title")}</h1>
      <div className="statistics__list">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">{t("admin.statistics.booked_sessions")}</h2>
          <CalendarIcon fill={getButtonTextColor()} />
        </div>
        <div className="statistics__list-content statistics__list-content--booked">
          <div className="card booked-session">
            <p className="card-title">
              {isLoading ? "-" : data?.total_sessions || 0}
            </p>
            <span className="card-description">
              {t("admin.statistics.total_sessions")}
            </span>
          </div>
          <div className="card booked-session">
            <p className="card-title">
              {isLoading
                ? "-"
                : data && `${data.confirmed_users || 0}/${data.total_users || 0}`}
            </p>
            <span className="card-description">{t("admin.statistics.total_users")}</span>
          </div>
          <div className="card booked-session">
            <p className="card-title">
              {isLoading ? "-" : data?.mentors || 0}
            </p>
            <span className="card-description">{t("admin.statistics.mentors")}</span>
          </div>
          <div className="card booked-session">
            <p className="card-title">
              {isLoading ? "-" : data?.mentees || 0}
            </p>
            <span className="card-description">{t("admin.statistics.mentees")}</span>
          </div>
        </div>
      </div>
      <div className="statistics__list">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">{t("admin.statistics.top_active_users")}</h2>
        </div>
        <div className="statistics__list-content">
          <div className="top-list">
            {data?.top_active_users ? data.top_active_users.map((user, index) => {
              return (
                <div key={user.user_id} className="top-list-item">
                  <p className="place">{index + 1}</p>
                  <div className="user">
                    <UserAvatar
                      avatarUrl={user.avatar_url}
                      fullName={user.name}
                      size={40}
                      shape="circle"
                      alt={user.name}
                      className="user-avatar"
                    />
                    <span className="user-name">{user.name}</span>
                  </div>
                </div>
              );
            }) : !isLoading && <span>{t("admin.statistics.no_user_statistics_data")}</span>}
            {isLoading && renderTopUsersSkeletons()}
          </div>
        </div>
      </div>
      <div className="statistics__list">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">
            {t("admin.statistics.top_distribution")}
          </h2>
        </div>
        <div className="statistics__list-content">
          <div className="top-list">
            {data?.top_distribution ? data.top_distribution.map((distribution, index) => {
              return (
                <div key={distribution.name} className="top-list-item">
                  <p className="place">{index + 1}</p>
                  <span className="distribution-name">{distribution.name}</span>
                </div>
              );
            }) : !isLoading && <span>{t("admin.statistics.no_distribution_statistics_data")}</span>}
            {isLoading && renderTopCategorySkeletons()}
          </div>
        </div>
      </div>
      {showQrAnalytics ? (
      <div className="statistics__list">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">{t("admin.statistics.qr_section_title")}</h2>
        </div>
        <div className="statistics__list-content statistics__list-content--qr">
          <div className="card booked-session">
            <p className="card-title">
              {isLoading ? "-" : data?.qr_analytics?.signups_total ?? 0}
            </p>
            <span className="card-description">{t("admin.statistics.qr_signups_total")}</span>
            <span className="statistics__qr-window">
              {t("admin.statistics.last_7_days", { count: data?.qr_analytics?.signups_7d ?? 0 })}
              {" · "}
              {t("admin.statistics.last_30_days", { count: data?.qr_analytics?.signups_30d ?? 0 })}
            </span>
          </div>
          <div className="card booked-session">
            <p className="card-title">
              {isLoading ? "-" : data?.qr_analytics?.bookings_total ?? 0}
            </p>
            <span className="card-description">{t("admin.statistics.qr_bookings_total")}</span>
            <span className="statistics__qr-window">
              {t("admin.statistics.last_7_days", { count: data?.qr_analytics?.bookings_7d ?? 0 })}
              {" · "}
              {t("admin.statistics.last_30_days", { count: data?.qr_analytics?.bookings_30d ?? 0 })}
            </span>
          </div>
        </div>
        {!isLoading && (data?.qr_analytics?.recent_signups?.length ?? 0) > 0 ? (
          <Table
            className="statistics__qr-signups-table"
            rowKey="user_id"
            data={data?.qr_analytics?.recent_signups ?? []}
            pagination={false}
            columns={[
              {
                title: t("admin.statistics.qr_col_name"),
                dataIndex: "name",
                render: (_: string, record) =>
                  renderModerationProfileLink(
                    record.user_id,
                    record.name,
                    record.last_name
                  ),
              },
              {
                title: t("admin.statistics.qr_col_email"),
                dataIndex: "email",
              },
              {
                title: t("admin.statistics.qr_col_date"),
                dataIndex: "created_at",
                render: (value: string) => formatModerationDateTime(value),
              },
            ]}
          />
        ) : null}
      </div>
      ) : null}
      {eventIntakeEnabled ? (
        <div className="statistics__list statistics__guest-section">
          <div className="statistics__list-header">
            <h2 className="statistics__list-header-title">{t("admin.statistics.guest_section_title")}</h2>
          </div>
          <div className="statistics__list-content statistics__list-content--qr">
            <div className="card booked-session">
              <p className="card-title">
                {isLoading ? "-" : data?.guests_total ?? 0}
              </p>
              <span className="card-description">{t("admin.statistics.guests_total")}</span>
            </div>
            <div className="card booked-session">
              <p className="card-title">
                {isLoading ? "-" : data?.guests_active ?? 0}
              </p>
              <span className="card-description">{t("admin.statistics.guests_active")}</span>
            </div>
          </div>
        </div>
      ) : null}
      <div className="statistics__list statistics__moderation-section">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">{t("admin.statistics.moderation_section_title")}</h2>
        </div>
        <div className="statistics__panel">
          <Table
            className="statistics__moderation-table"
            rowKey="id"
            loading={moderationLoading}
            data={visibleModerationData}
            columns={moderationColumns}
            scroll={{ x: statsScrollX }}
            pagination={{
              pageSize: MODERATION_PAGE_SIZE,
              current: moderationPage,
              onChange: (page) => setModerationPage(page),
              hideOnSinglePage: true,
              size: "mini",
            }}
            noDataElement={t("admin.moderation.no_data")}
          />
        </div>
      </div>
      <ScheduledPostsSection />
      <div className="statistics__list statistics__reports-section">
        <div className="statistics__list-header">
          <h2 className="statistics__list-header-title">{t("admin.statistics.reports_section_title")}</h2>
        </div>
        <div className="statistics__panel statistics__reports-panel">
          <div className="statistics__reports-controls">
            <ReportsTypePicker
              availableReportTypes={availableReportTypes}
              selectedReports={selectedReports}
              onChange={setSelectedReports}
            />
            <DatePicker.RangePicker
              className="statistics__reports-range"
              format="M/D/YY"
              placeholder={[
                t("admin.statistics.report_date_from"),
                t("admin.statistics.report_date_to"),
              ]}
              allowClear
              getPopupContainer={() => document.body}
              value={reportRange as [Dayjs, Dayjs] | undefined}
              onChange={(value) => {
                if (!value || !value[0] || !value[1]) {
                  setReportRange(undefined);
                  return;
                }
                setReportRange([dayjs(value[0]), dayjs(value[1])]);
              }}
            />
            <div className="statistics__reports-full-wrap">
              <Button
                shape="round"
                disabled={isDownloadingReport}
                onClick={handleFullReport}
                className="statistics__reports-full"
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                }}
              >
                {t("admin.statistics.reports_full_report")}
              </Button>
            </div>
            <div className="statistics__reports-download-wrap">
              <Button
                shape="round"
                disabled={selectedReports.length === 0 || isDownloadingReport}
                onClick={handleDownloadReport}
                className="statistics__reports-download"
                style={{
                  color: getButtonTextColor(),
                  backgroundColor: getButtonColor(),
                }}
              >
                {t("admin.statistics.reports_download")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
